import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useGetSimulationCollectionsQuery, useDeleteSimulationCollectionMutation } from "../../store/services/simulation-service";
import { useSelector } from "react-redux";
import {
    Alert,
    Button,
    IconButton,
    Dialog,
    DialogContent,
    Snackbar,
    Box,
    CircularProgress
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SimulationCollectionPreview from "./SimulationCollectionPreview";
import Table from "../../components/Table/Table";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import PhishingIcon from '@mui/icons-material/Phishing';
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import { useTheme } from "@mui/material/styles";
import SharingDisplay from '../../components/ContentSharing/SharingDisplay';
import StyledChip from '../../components/Chips/StyledChip'; 
import { ModalProvider } from '../../components/Modal/ModalContext';


SimulationCollectionTable.propTypes = {
    type: PropTypes.oneOf(["edit", "select"]).isRequired,
    collectionIds: PropTypes.array,
    setCollectionId: PropTypes.func,
};

export default function SimulationCollectionTable(props) {
    const { type, collectionIds, setCollectionId, onSpecificSelect } = props;
    const navigate = useNavigate();
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    const { tenancyId } = useSelector((state) => state.tenancy);

    const [isAdmin, setIsAdmin] = useState(false);
    const [isTenancyAdmin, setIsTenancyAdmin] = useState(false);
    const [isUser, setIsUser] = useState(false);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setIsAdmin(user && user.role === "Administrators");
        setIsTenancyAdmin(user && user?.role?.includes("TenancyAdministrators"));
        setIsUser(user && user.role === "");
    }, []);

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });

    const { data: collectionData, isLoading: collectionsLoading, error: collectionsError } = useGetSimulationCollectionsQuery({
        perPage: paginationModel.pageSize,
        page: paginationModel.page,
        searchTerm: "",
        tenancyId: tenancyId,
        published: true
    });

    const sortedCollectionData = useMemo(() => {
        if (!collectionData) return collectionData;

        const dataToSort = collectionData?.data || collectionData;
        if (!Array.isArray(dataToSort)) return collectionData;

        const sorted = [...dataToSort].sort((a, b) => {
            const nameA = (a.collectionName || '').toLowerCase();
            const nameB = (b.collectionName || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });

        if (collectionData?.data) {
            return {
                ...collectionData,
                data: sorted
            };
        }

        return sorted;
    }, [collectionData]);

    const [deleteCollection] = useDeleteSimulationCollectionMutation();

    const [selectedCollection, setSelectedCollection] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [simulationPreviewOpen, setSimulationPreviewOpen] = useState(false);
    const [selected, setSelected] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    
    const [deletingId, setDeletingId] = useState(null);

    const handleAddCollection = () => {
        navigate("/simulation-collections/add");
    };

    const handleViewCollection = (collection) => {
        navigate(`/simulation-collections/${collection.internalId}`);
    };

    const handleEditCollection = (collection) => {
        navigate(`/simulation-collections/edit/${collection.internalId}`);
    };

    const handleDeleteCollection = async (collectionId) => {
        try {
            setDeletingId(collectionId);
            await deleteCollection(collectionId).unwrap();
            setSnackbarMessage("Collection deleted successfully");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            setSnackbarMessage("Error deleting collection");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setDeletingId(null);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleSelectCollection = (collectionId) => {
        setSelected(collectionId);
    };

    const handlePreview = (row) => {
        setSelectedCollection(row);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        // Only close if simulation preview is not open
        if (!simulationPreviewOpen) {
            setModalOpen(false);
            setSelectedCollection(null);
        }
    };

    const handleSpecificSelection = () => {
        if (onSpecificSelect) {
            onSpecificSelect();
        }
    };

    const toolbarActions = [
        {
            render: () => (
                <Box width={"100%"} justifyContent={"flex-end"} display={"flex"}>
                    {type === "select" && (
                        <Button
                            size="medium"
                            variant="contained"
                            color="warning"
                            sx={{ textTransform: "capitalize", mr: 2 }}
                            onClick={handleSpecificSelection}
                            endIcon={<MenuOpenIcon />}
                        >
                            List
                        </Button>
                    )}
                    {type === "edit" && !isUser && (
                        <Button
                            size="medium"
                            variant="contained"
                            sx={{ textTransform: "capitalize" }}
                            onClick={handleAddCollection}
                            endIcon={<AddIcon />}
                        >
                            Add Collection
                        </Button>
                    )}

                </Box>
            )
        }
    ];

    let headCells = [
        {
            id: "select",
            label: "Select",
            render: (dataRow) => {
                const isSelected = collectionIds?.map(col => col.internalId).includes(dataRow.internalId);
                return (
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (type === "select") {
                                // For select mode, enforce single selection
                                if (isSelected) {
                                    setCollectionId([]);
                                } else {
                                    setCollectionId([dataRow]); // Always replace with single selection
                                }
                            } else {
                                // For edit mode, allow multiple selection if needed in the future
                                if (isSelected) {
                                    setCollectionId(collectionIds?.filter(col => col.internalId !== dataRow.internalId));
                                } else {
                                    setCollectionId([...collectionIds, dataRow]);
                                }
                            }
                        }}
                    >
                        {isSelected ? <CheckBoxRoundedIcon color="warning" /> : <CheckBoxOutlineBlankRoundedIcon color={lightMode ? "black" : "white"} />}
                    </IconButton>
                );
            },
            initialVisible: type === "select"
        },
        {
            id: "preview",
            label: "Preview",
            render: (dataRow) => (
                <Button onClick={() => handlePreview(dataRow)} size="small" variant="contained" color="warning">
                    Preview
                </Button>
            ),
            initialVisible: true
        },
        {
            id: "edit",
            label: "Edit",
            render: (dataRow) => {
                const isVisible = isAdmin || (isTenancyAdmin && dataRow.sharedTenancyIds.includes(Number(tenancyId)));

                return (
                    <Button
                        onClick={() => handleEditCollection(dataRow)}
                        size="small"
                        variant="contained"
                        color="secondary"
                        sx={{ display: isVisible ? "block" : "none" }}
                    >
                        Edit
                    </Button>
                );
            },
            initialVisible: type === "edit"
        },
        {
            id: "collectionName",
            label: "Collection Name",
            initialVisible: true
        },
        {
            id: "simulationCount",
            label: "Collection Size",
            render: (dataRow) => (
                <StyledChip
                    label={`${dataRow.simulationIds?.length || 0} Simulations`}
                    size="small"
                    color={theme.palette.secondary.main}
                    icon={<PhishingIcon />} 
                    variant="filled"
                />
            ),
            initialVisible: true
        },
        {
            id: "sharing",
            label: "Sharing",
            render: (dataRow) => (
                <SharingDisplay
                    published={dataRow.published}
                    sharedTenancyIds={dataRow.sharedTenancyIds}
                    publishToFamily={dataRow.publishToFamily}
                    variant="compact"
                />
            ),
            initialVisible: true
        },
        {
            id: "dateCreated",
            label: "Created",
            render: (dataRow) => new Date(dataRow.dateCreated).toLocaleDateString("en-GB"),
            initialVisible: type === "edit"
        },
        {
            id: "dateUpdated",
            label: "Last Updated",
            render: (dataRow) => new Date(dataRow.dateUpdated).toLocaleDateString("en-GB"),
            initialVisible: true
        },
        {
            id: "action",
            label: "",
            initialVisible: type === "edit",
            render: (dataRow) => {
                if (dataRow.sharedTenancyIds.includes(0) && !isAdmin) return <></>;

                return (
                    <Button
                        variant="contained"
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCollection(dataRow.internalId)}
                        disabled={deletingId === dataRow.internalId}
                        sx={{
                            minWidth: '70px',
                            width: '70px',
                            height: '32px',
                            padding: '6px 8px',
                            '& .MuiCircularProgress-root': {
                                marginRight: 0
                            }
                        }}
                    >
                        {deletingId === dataRow.internalId ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "Delete"
                        )}
                    </Button>
                );
            },

        }
    ];

    return (
        <>
            <Table
                name={"Simulation Collection"}
                data={sortedCollectionData?.data || sortedCollectionData}
                error={collectionsError}
                loading={collectionsLoading}
                headCells={headCells}
                search={true}
                editColumn={false}
                checkbox={false}
                toolbarActions={toolbarActions}
                showToolbarActions={true}
                onDelete={handleDeleteCollection}
                showDelete={isAdmin || isTenancyAdmin}
                onCellClick={handleViewCollection}
                selected={selected}
                setSelected={handleSelectCollection}
                maxTableHeight={type === "select" ? "calc(100vh - 350px)" : "calc(100vh - 250px)"}
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />

            {/* Collection Preview Modal with modal management */}
            <ModalProvider>
                <Dialog
                    open={modalOpen}
                    onClose={handleCloseModal}
                    maxWidth={false}
                    PaperProps={{
                        sx: {
                            width: '70%',
                            height: '80%',
                            margin: '0 auto',
                            backgroundColor: theme.palette.background.default,
                            borderRadius: '8px',
                            overflow: 'hidden',
                            position: 'relative',
                            visibility: simulationPreviewOpen ? 'hidden' : 'visible',
                        }
                    }}
                    sx={{
                        '& .MuiDialog-container': {
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100vh'
                        },
                        '& .MuiPaper-root': {
                            m: 0
                        },
                        '& .MuiBackdrop-root': {
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        }
                    }}
                >
                    {/* Close button */}
                    <IconButton
                        onClick={() => {
                            setModalOpen(false);
                            setSelectedCollection(null);
                            // Reset simulation preview state when closing
                            setSimulationPreviewOpen(false);
                        }}
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: -45,
                            bgcolor: theme.palette.background.default,
                            boxShadow: 2,
                            '&:hover': {
                                bgcolor: theme.palette.background.default,
                                opacity: 0.9
                            },
                            width: 40,
                            height: 40,
                            zIndex: (theme) => theme.zIndex.modal + 1
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <DialogContent
                        sx={{
                            p: 0,
                            height: '100%',
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        {modalOpen && selectedCollection && (
                            <SimulationCollectionPreview
                                collectionId={selectedCollection.internalId}
                                setSimulationPreviewOpen={setSimulationPreviewOpen}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </ModalProvider>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}